import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment/moment";
import Swal from "sweetalert2";
import { ThreeDots } from 'react-loader-spinner';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  ButtonGroup,
  Button,
  Image
} from "react-bootstrap";
import { 
  getList, 
  updateReview, 
  reviewWithdrawal, 
  resetReview,
  getMedicalCert,
  resetCert,
} from "../../3.Store/LeaveWithdrawalSlice";
import { getSpecialLeaveAttachment } from "../../3.Store/ReviewLeaveApplicationSlice";
import PDFViewer from "../../2.Commons/PDFViewer";

const LeaveWithdrawalForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const detail = useSelector(state => state.LeaveWithdrawalSlice.withdrawlist);
  const list = useSelector(state => state.LeaveWithdrawalSlice.list);
  const { remark_withdrawal } = list;
  const cert = useSelector(state => state.LeaveWithdrawalSlice.cert);
  const [loading, setLoading] = useState(false);
  const special_attachment = useSelector(state => state.ReviewLeaveApplicationSlice.special_attachment);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      dispatch(getList(id));
      const entity = { ...list, id: id };
      dispatch(updateReview(entity));
    } else {
      dispatch(resetReview());
    };
  }, [id])

  useEffect(() => {
    // Function to fetch data based on leave type
    const fetchData = async () => {
      try {
        setLoading(true); // Set loading to true when useEffect starts

        if (detail.leave_application.leave_type.id === "SI") {
          await dispatch(getMedicalCert({ id: detail.leave_application.id }));
        } else if (detail.leave_application.leave_type?.id === "SP") {
          await dispatch(getSpecialLeaveAttachment({ id: detail.leave_application.id }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false); // Set loading to false after dispatch completes (success or failure)
      }
    };
    fetchData();
  }, [detail?.leave_application?.leave_type?.id]);
  
  const handleRemarkChange = (e) => {
    const entity = { ...list, remark_withdrawal: e.target.value };
    console.log(entity)
    dispatch(updateReview(entity));
  };

  const handleBtnBack = () => {
    dispatch(resetReview());
    dispatch(resetCert());
    navigate(`/LeaveWithdrawalList`);
  };

  const handleBtnReject = () => {
    Swal.fire({
        title: 'Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        reverseButtons: true
    }).then((result) => {
        if (result.isConfirmed) {
          setLoading(true);
          const entity = { ...list, status: { id: "-200" } };
          dispatch(reviewWithdrawal(entity))
            .unwrap()
            .then(() => {
              setLoading(false);
              Swal.fire("Withdrawal Rejected", "", "success", "OK")
              .then(() => {
                // dispatch(getListsOfLeaveWithdrawal());
                navigate(`/LeaveWithdrawalList`);
              })
            })
            .catch((err) => {
              Swal.fire("Error", err.response.data, "error", "OK");
            });
        } else if (result.dismiss === Swal.DismissReason.cancel) {
            Swal.fire(
              'Cancelled',
              'The rejection has been cancelled',
              'error'
            );
        }
    });
  };

  const handleBtnApprove = () => {
    Swal.fire({
        title: 'Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        reverseButtons: true
    }).then((result) => {
        if (result.isConfirmed) {
          setLoading(true);
          const entity = { ...list, status: { id: "200" } };
          dispatch(reviewWithdrawal(entity))
            .unwrap()
            .then(() => {
              setLoading(false);
              Swal.fire("Withdrawal Approved", "", "success", "OK")
              .then(() => {
                // dispatch(getListsOfLeaveWithdrawal());
                navigate(`/LeaveWithdrawalList`);
              })
            })
            .catch((err) => {
              Swal.fire("Error", err.response.data, "error", "OK");
            })
            .finally(() => {
              setLoading(false);
            });
        } else if (result.dismiss === Swal.DismissReason.cancel) {
            Swal.fire(
              'Cancelled',
              'The approval has been cancelled',
              'error'
            );
        }
    });
  };

  return (
    <Container className="ContainerContent">
      {loading && (
        <ThreeDots
          visible={loading}
          height="80"
          width="80"
          color="#4fa94d"
          radius="9"
          ariaLabel="three-dots-loading"
          wrapperStyle={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: '9999',
          }}
        />
      )}
      <Card className="CardContent2">
        <div className="d-flex justify-content-between">
          <ButtonGroup className="my-2">
            <Button className="Button" onClick={handleBtnBack}>
              Back
            </Button>
          </ButtonGroup>
        </div>
        <h1 className="TitleHeader">HR Leave Withdrawal Request</h1>
        <h1 className="MiniTitleHeader">View</h1>

        <Row className="my-3">
          <Col md={6} sm={6}>
            <Form.Label className="FormLabel fw-normal fs-5">
              Withdrawal Requested by
            </Form.Label>
          </Col>
          <Col md={6} sm={6}>
            <Form.Control value={detail?.leave_application.employee.fullname} readOnly disabled/>
          </Col>
        </Row>

        <Row className="my-3">
          <Col md={6} sm={6}>
            <Form.Label className="FormLabel fw-normal fs-5">
              Withdrawal Requested on
            </Form.Label>
          </Col>
          <Col md={6} sm={6}>
            <Form.Control value={moment(detail?.created).format("dddd, MMMM DD, YYYY")} readOnly disabled/>
          </Col>
        </Row>

        <Row className="my-3">
          <Col md={12} sm={12}>
            <Form.Label className="FormLabel fw-normal fs-5">Reason For Withdrawal</Form.Label>
            <Form.Control as="textarea" value={detail?.reason} readOnly disabled/>
          </Col>
        </Row>

        <Row className="my-3">
          <Col md={12} sm={12}>
            <Form.Label className="FormLabel fw-normal fs-5">
              HR Remark For Withdrawal
            </Form.Label>
            <Form.Control 
              as="textarea"
              name="remark_withdrawal"
              value={remark_withdrawal || detail?.remark}
              onChange={handleRemarkChange}
            />
          </Col>
        </Row>

        <hr />

        <Row className="my-3">
          <Col md={6} sm={6}>
            <Form.Label className="FormLabel fw-normal fs-5">Leave Type</Form.Label>
          </Col>
          <Col md={6} sm={6}>
            <Form.Control value={detail?.leave_application.leave_type.description} readOnly disabled/>
          </Col>
        </Row>

        {/* Leave Advanced */}
        {detail?.leave_application.leave_type.description == "Advance Leave" && (
          <Row className="my-2">
            <Col md={6} sm={6}>
              <Form.Label className="FormLabel fw-normal fs-5">Leave Advanced</Form.Label>
            </Col>
            <Col md={6} sm={6}>
              <Form.Control />
            </Col>
          </Row>
        )}

        {/* Sick Leave */}
        {detail?.leave_application.leave_type.description === "Sick Leave" && (
          <Row className="my-2">
            {/* <img src={cert?.file} alt="Medical Certificate" /><br /><br /> */}
            {cert?.filetype?.startsWith('image/') ? (
              <Image
                src={
                  cert.file.startsWith("data:")
                    ? cert.file
                    : `data:${cert.filetype};base64,${cert.file}`
                }
                alt="Uploaded file"
                fluid
              />
            ) : (
              // Display PDF
              cert.file ? (
                <div style={{ width: "100%" }}>
                  <PDFViewer base64Data={cert.file} height="500px" width="100%" />
                </div>
              ) : (
                <a href={`data:application/pdf;base64,${cert.file}`} download="attachment.pdf">
                  Download PDF
                </a>
              )
            )}
          </Row>
        )}

        {/* Special Leave */}
        {detail?.leave_application.leave_type.description == "Special Leave" && (
          <>
            <Row className="my-2">
              <Col md={6} sm={6}>
                <Form.Label className="FormLabel fw-normal fs-5">
                  Special Leave Type
                </Form.Label>
              </Col>
              <Col md={6} sm={6}>
                <Form.Control value={detail?.leave_application?.special_leave_type?.description} disabled/>
              </Col>
            </Row>
          </>
        )}

        {/* Special Leave */}
        {detail?.leave_application.leave_type.description === "Special Leave" && special_attachment && special_attachment.file && (
          <Row className="my-2">
            {/* <img src={cert?.file} alt="Medical Certificate" /><br /><br /> */}
            {special_attachment?.filetype?.startsWith('image/') ? (
              <Image
                src={
                  special_attachment.file.startsWith("data:")
                    ? special_attachment.file
                    : `data:${special_attachment.filetype};base64,${special_attachment.file}`
                }
                alt="Uploaded file"
                fluid
              />
            ) : (
              // Display PDF
              special_attachment.file ? (
                <div style={{ width: "100%" }}>
                  <PDFViewer base64Data={special_attachment.file} height="500px" width="100%" />
                </div>
              ) : (
                <a href={`data:application/pdf;base64,${special_attachment.file}`} download="attachment.pdf">
                  Download PDF
                </a>
              )
            )}
          </Row>
        )}

        {/* <Row className="my-2">
          <Col md={6} sm={6}>
            <Form.Label className="FormLabel fw-normal fs-5">No. of Days</Form.Label>
          </Col>
          <Col md={6} sm={6}>
            <Form.Control value={detail?.leave_application.numberofday_type.description} readOnly disabled/>
          </Col>
        </Row> */}

        {/* <Row className="my-2">
          <Col md={6} sm={6}>
            <Form.Label className="FormLabel fw-normal fs-5">From</Form.Label>
          </Col>
          {detail?.leave_application.numberofday_type.description != "One Day" && (
            <Col md={3} sm={6}>
              <Form.Check label="AM" checked={detail?.leave_application.from_ampm == "AM"} style={{ color: "#fff" }} readOnly/>
              <Form.Check label="PM" checked={detail?.leave_application.from_ampm == "PM"} style={{ color: "#fff" }} readOnly/>
            </Col>
          )}
          <Col md={3} sm={6}>
            <Form.Control value={moment(detail?.leave_application.from_date.date).format("YYYY-MM-DD")} readOnly disabled/>
          </Col>
        </Row>
        {detail?.leave_application.numberofday_type.description == "More than One Day" && (
          <Row className="my-2">
            <Col md={6} sm={6}>
              <Form.Label className="FormLabel fw-normal fs-5">To</Form.Label>
            </Col>
            <Col md={3} sm={6}>
              <Form.Check label="AM" checked={detail?.leave_application.to_ampm == "AM"} style={{ color: "#fff" }} readOnly/>
              <Form.Check label="PM" checked={detail?.leave_application.to_ampm == "PM"} style={{ color: "#fff" }} readOnly/>
            </Col>
            <Col md={3} sm={6}>
              <Form.Control value={moment(detail?.leave_application.to_date.date).format("YYYY-MM-DD")} readOnly disabled/>
            </Col>
          </Row>
        )} */}

        <Row className="my-3">
          <Col md={6} sm={4}>
            <Form.Label className="FormLabel fw-normal fs-5">From</Form.Label>
          </Col>
          <Col md={2} sm={4}>
            <div>
              <Form.Check
                type="radio"
                label="AM"
                name="from_ampm"
                value="AM"
                style={{ color: "#fff" }}
                checked={detail?.leave_application.from_ampm === "AM"}
                disabled
              />
              <Form.Check
                type="radio"
                label="PM"
                name="from_ampm"
                value="PM"
                style={{ color: "#fff" }}
                checked={detail?.leave_application.from_ampm === "PM"}
                disabled
              />
            </div>
          </Col>
          <Col md={4} sm={4}>
            <Form.Control
              type="date"
              name="from_date"
              value={moment(detail?.leave_application.from_date.date).format("YYYY-MM-DD")}
              disabled
            />
          </Col>
        </Row>

        <Row className="my-2">
          <Col md={6} sm={4}>
            <Form.Label className="FormLabel fw-normal fs-5">To</Form.Label>
          </Col>
          <Col md={2} sm={4}>
            <div>
              <Form.Check
                type="radio"
                label="AM"
                name="to_ampm"
                value="AM"
                style={{ color: "#fff" }}
                checked={detail?.leave_application.to_ampm === "AM"}
                disabled
              />
              <Form.Check
                type="radio"
                label="PM"
                name="to_ampm"
                value="PM"
                style={{ color: "#fff" }}
                checked={detail?.leave_application.to_ampm === "PM"}
                disabled
              />
            </div>
          </Col>
          <Col md={4} sm={4}>
            <Form.Control
              type="date"
              name="to_date"
              value={moment(detail?.leave_application.to_date.date).format("YYYY-MM-DD")}
              disabled
            />
          </Col>
        </Row>

        <Row className="my-2">
          <Col md={6} sm={6}>
            <Form.Label className="FormLabel fw-normal fs-5">Location</Form.Label>
          </Col>
          <Col md={6} sm={6}>
            <Form.Control value={detail?.leave_application.location_type.description} readOnly disabled/>
          </Col>
        </Row>

        <Row className="my-2">
          <Col md={6} sm={6}>
            <Form.Label className="FormLabel fw-normal fs-5">No. Days Applying</Form.Label>
          </Col>
          <Col md={6} sm={6}>
            <Form.Control value={detail?.leave_application.number_of_days} readOnly disabled/>
          </Col>
        </Row>

        <Row className="my-2">
          <Col md={6} sm={6}>
            <Form.Label className="FormLabel fw-normal fs-5">Description</Form.Label>
          </Col>
          <Col md={6} sm={6}>
            <Form.Control as="textarea" rows={3} value={detail?.leave_application.description} readOnly disabled/>
          </Col>
        </Row>

        <Row className="my-2">
          <Col md={6} sm={6}>
            <Form.Label className="FormLabel fw-normal fs-5">Reason</Form.Label>
          </Col>
          <Col md={6} sm={6}>
            <Form.Control as="textarea" rows={3} value={detail?.leave_application.reason_for_application} readOnly disabled/>
          </Col>
        </Row>

        <Row className="my-2">
          <Col md={6} sm={6}>
            <Form.Label className="FormLabel fw-normal fs-5">Remark</Form.Label>
          </Col>
          <Col md={6} sm={6}>
            <Form.Control as="textarea" rows={3} value={detail?.leave_application.remark} readOnly disabled/>
          </Col>
        </Row>

        <div className="d-flex justify-content-between">
          <ButtonGroup className="my-2">
            <Button className="Button" onClick={handleBtnBack}>
              Back
            </Button>
          </ButtonGroup>

          <ButtonGroup className="my-2">
            {detail?.leave_application.status.short_description === "WITHDRAWING" && (
              <>
                <Button className="Button" onClick={handleBtnReject}>
                  Reject
                </Button>
                &nbsp; &nbsp;
                <Button className="Button" onClick={handleBtnApprove}>
                  Approve
                </Button>
              </>
            )}
            {/* <Button className="Button" onClick={handleBtnReject}>
              Reject
            </Button>
            &nbsp; &nbsp;
            <Button className="Button" onClick={handleBtnApprove}>
              Approve
            </Button> */}
          </ButtonGroup>
        </div>
      </Card>
    </Container>
  );
};
export default LeaveWithdrawalForm;